import React, { type ReactNode } from 'react';
import { EntryPointContainer } from 'react-relay';
import { SPA_CONTENT } from '@atlassian/jira-entry-point-page-layout-fields/src/index.tsx';
import { EntryPointReferencePlaceholder } from '@atlassian/jira-entry-point-placeholder/src/index.tsx';
import { usePageLayoutEntryPoint } from '@atlassian/jira-entry-point-page-layout/src/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { jiraSpaPageContainerResource } from '@atlassian/jira-router-resources-spa-page-container/src/jiraSpaPageContainerResource.tsx';
import { ReportSSRFailOnUse } from '@atlassian/jira-ssr-render-guard/src/index.tsx';
import type { spaEntryPoint } from '../../../../entrypoint.tsx';
import type { PageContainerProps } from './types.tsx';
import { JiraLogoPlaceholder } from './JiraLogoPlaceholder.tsx';
import { JiraSpaFallbackLoader as LoadViaFallback } from './JiraSpaFallbackLoader.tsx';
import { JiraSpaPageContainerOuter } from './JiraSpaPageContainerOuter.tsx';
import { JiraSpaPageContainer as JiraSpaPageContainerInner } from './JiraSpaPageContainer.tsx';

// Use react resource router to load a relay query.
const LoadViaResource = (props: { children: ReactNode }) => {
	const { queryReference } = useRelayResource(jiraSpaPageContainerResource);
	// This intermediate step is to avoid using a null queryReference.
	// The placeholder should only render when (1) the page was not rendered via SSR, and (2) the route does not declare the spa query as a route resource dependency
	return queryReference ? (
		<JiraSpaPageContainerInner queryReference={queryReference}>
			{props.children}
		</JiraSpaPageContainerInner>
	) : (
		<LoadViaFallback {...props} />
	);
};

const LoadViaPageEntrypoint = (props: { children: ReactNode }) => {
	const spaEntryPointReference = usePageLayoutEntryPoint<typeof spaEntryPoint>(SPA_CONTENT);
	return spaEntryPointReference ? (
		<EntryPointReferencePlaceholder
			name="spa_skeleton"
			fallback={
				<>
					<JiraLogoPlaceholder />
					<ReportSSRFailOnUse pageId="spa" fallbackId="SpaEntryPoint" displayErrorInStaging />
				</>
			}
			entryPointReference={spaEntryPointReference}
			isSSRPlaceholderEnabled={false}
		>
			<EntryPointContainer entryPointReference={spaEntryPointReference} props={props} />
		</EntryPointReferencePlaceholder>
	) : (
		<LoadViaResource>{props.children}</LoadViaResource>
	);
};

/**
 * Kicks off the render for the Jira SPA page layout.
 * The Jira app uses an islands architecture made up of multiple (nested) page entrypoints.
 * Each is expected to render its own suspense boundary and loading skeleton.
 *
 * @note
 * The page layout has its own data query, which is preloaded during SSR.
 * This component attempts to load the query via entrypoint definition first.
 * If that does not exist, it falls back to a route-resource method.
 * If that does not work, it falls back to rendering a placeholder component and triggering the query loading lazily.
 *
 * @note
 * When altering how this component or any of its children render, be sure you test the following scenarios:
 *
 * 1. A page created using the `createPageEntryPoint` utility (e.g., issueview)
 * 2. A page created via the `createChromelessEntryPoint` utility (e.g., most embed routes)
 * 3. A "legacy" page rendering via monolith (e.g., the /plugins/servlet/upm route)
 *
 * Also test that the page renders correctly when various SSR scenarios have occurred:
 *
 * 1. Full SSR success
 * 2. Static fallback
 * 3. SSR shell renders, route fails
 * 4. SSR shell renders, route partially renders
 */
export const JiraSpaPageContainerBootstrapper = ({ children, ...rest }: PageContainerProps) => {
	return (
		<JiraSpaPageContainerOuter {...rest}>
			<LoadViaPageEntrypoint>{children}</LoadViaPageEntrypoint>
		</JiraSpaPageContainerOuter>
	);
};
